import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/components/utils/translations';

// Australia data
const dataAustralia2024 = [
  { category: "Lecture Capture / Video CMS", count: 2, examples: "ANU & Curtin (EchoVideo Cloud)", color: "#0A1A2F" },
  { category: "Career Services", count: 1, examples: "ANU (Target Connect)", color: "#1C485C" },
  { category: "AI & Productivity", count: 1, examples: "ANU (Microsoft Copilot)", color: "#3D8C91" },
  { category: "Campus Mobile ID", count: 1, examples: "USyd (Transact Mobile Credential)", color: "#56B0AF" },
  { category: "Accessibility", count: 1, examples: "UOW (Brickfield Toolkit)", color: "#89CDD4" },
  { category: "Teaching Evaluation", count: 1, examples: "UTS (Explorance Blue)", color: "#C6F2F0" },
  { category: "Engagement & Polling", count: 1, examples: "UON (Poll Everywhere)", color: "#ECFFFE" }
];

const dataAustralia2025 = [
  { category: "Online Assessment", count: 5, examples: "Curtin (Respondus); ANU (Inspera, Cadmus); ACU & Bond (Respondus)", color: "#0A1A2F" },
  { category: "LMS", count: 4, examples: "ACU (Canvas); CDU & Curtin (Learn Ultra)", color: "#1C485C" },
  { category: "Video CMS", count: 4, examples: "CSU & Bond (Panopto); CDU (Kaltura); CQU (Echo360 Cloud)", color: "#3D8C91" },
  { category: "Engagement & Polling", count: 2, examples: "Curtin (Poll Everywhere, Mentimeter)", color: "#56B0AF" },
  { category: "Research & ERP", count: 2, examples: "UoM (Cayuse + Rimini Street)", color: "#89CDD4" }
];

// Hong Kong data
const dataHongKong2024 = [
  { category: "Assessment Automation", count: 2, percentage: 28.6, examples: "FeedbackFruits pilots at CityU & HKBU", color: "#0A1A2F" },
  { category: "Library AI / Discovery", count: 1, percentage: 14.3, examples: "Ex Libris Primo RA (CityU)", color: "#1C485C" },
  { category: "Library Reading Lists", count: 1, percentage: 14.3, examples: "CUHK", color: "#3D8C91" },
  { category: "Identity & Access Management", count: 1, percentage: 14.3, examples: "Okta (CityU)", color: "#56B0AF" },
  { category: "Collaboration", count: 1, percentage: 14.3, examples: "Zoom (CUHK)", color: "#89CDD4" },
  { category: "Productivity AI", count: 1, percentage: 14.3, examples: "Microsoft Copilot (HKBU)", color: "#C6F2F0" }
];

const dataHongKong2025 = [
  { category: "Assessment Automation", count: 3, percentage: 50.0, examples: "Turnitin Gradescope/FBS pilots (HSUHK, EdUHK)", color: "#0A1A2F" },
  { category: "Library AI / Discovery", count: 2, percentage: 33.3, examples: "Ex Libris overlays (HKU, SFU)", color: "#1C485C" },
  { category: "Research Information (CRIS)", count: 1, percentage: 16.7, examples: "Elsevier Pure (HKU)", color: "#3D8C91" }
];

// United Kingdom data
const dataUK2024 = [
  { category: "Course & Teaching Evaluation", count: 3, percentage: 17.6, examples: "Explorance Blue (Cardiff, Aston); EvaSys (Cumbria)", color: "#0A1A2F" },
  { category: "Cybersecurity / Compliance", count: 2, percentage: 11.8, examples: "Fortinet (Aberdeen); Secure Nexus (Dundee)", color: "#1C485C" },
  { category: "Student Success & Engagement", count: 2, percentage: 11.8, examples: "Corporate Project Solutions (Aston); Unibuddy (Brunel)", color: "#3D8C91" },
  { category: "Career Services", count: 1, percentage: 5.9, examples: "Symplicity MyCareerHub (Aberdeen)", color: "#56B0AF" },
  { category: "AI & Virtual Assistants", count: 1, percentage: 5.9, examples: "FUTR AI (Aberdeen)", color: "#89CDD4" },
  { category: "Virtual Labs & STEM", count: 1, percentage: 5.9, examples: "Virtual Dissection Table (Aberdeen)", color: "#C6F2F0" },
  { category: "Online Assessment", count: 1, percentage: 5.9, examples: "Questionmark OnDemand (Edinburgh)", color: "#0B0F1A" },
  { category: "Admissions / CRM", count: 1, percentage: 5.9, examples: "QS Admissions CRM (Edinburgh)", color: "#102838" },
  { category: "Classroom Engagement", count: 1, percentage: 5.9, examples: "Wooclap (Edinburgh)", color: "#286D7F" },
  { category: "Research & Data Mgmt", count: 1, percentage: 5.9, examples: "Elsevier Pure (Cumbria)", color: "#3D8C91" },
  { category: "ITSM", count: 1, percentage: 5.9, examples: "ITSM via Softcat (Cumbria)", color: "#56B0AF" },
  { category: "Lecture Capture", count: 1, percentage: 5.9, examples: "Panopto (UCA)", color: "#89CDD4" }
];

const dataUK2025 = [
  { category: "Payments & Financial Aid", count: 3, percentage: 20.0, examples: "Flywire (Edinburgh); EasyTransfer (Durham, LBU)", color: "#0A1A2F" },
  { category: "Lecture Capture / Video CMS", count: 2, percentage: 13.3, examples: "Panopto (Bristol, UCA)", color: "#1C485C" },
  { category: "Integration & Data Platforms", count: 2, percentage: 13.3, examples: "Elastic UK (Bedfordshire); WSO2 Choreo (Edinburgh)", color: "#3D8C91" },
  { category: "Cybersecurity", count: 1, percentage: 6.7, examples: "Cybersecurity (Bedfordshire)", color: "#56B0AF" },
  { category: "Classroom Engagement", count: 1, percentage: 6.7, examples: "Vevox (Aberdeen)", color: "#89CDD4" },
  { category: "Research Ethics", count: 1, percentage: 6.7, examples: "Worktribe Ethics (Leeds Beckett)", color: "#C6F2F0" },
  { category: "Course & Teaching Evaluation", count: 1, percentage: 6.7, examples: "Explorance Blue+MLY (West London)", color: "#0B0F1A" },
  { category: "Campus Mobile ID", count: 1, percentage: 6.7, examples: "Time2Eat (Brunel)", color: "#102838" },
  { category: "Student Housing", count: 1, percentage: 6.7, examples: "Kinetic Kx Cloud (Edinburgh)", color: "#286D7F" },
  { category: "Peer Review / Learning", count: 1, percentage: 6.7, examples: "FeedbackFruits (Durham)", color: "#3D8C91" }
];

// South Africa data
const dataSouthAfrica2024 = [
  { category: "Research & Data Management (CRIS/RIMS)", count: 4, examples: "Converis (WSU, DUT), Elsevier Pure (UJ)", color: "#0A1A2F" },
  { category: "CRM / Student Engagement", count: 3, examples: "Anthology CRM (NWU), Slate (UCT-GSB), Anthology Reach (UP)", color: "#1C485C" },
  { category: "Library Systems", count: 2, examples: "Ex Libris Alma + Primo (SMU, UFS)", color: "#3D8C91" },
  { category: "Cybersecurity", count: 1, examples: "Mimecast + SOC (WSU)", color: "#56B0AF" },
  { category: "AI & Academic Tools", count: 2, examples: "Grammarly (WSU), MoUJi Chatbot (UJ), FeedbackFruits (RU)", color: "#89CDD4" },
  { category: "Digital Credentials", count: 2, examples: "Parchment CeCredentialTrust (SU), Anthology Milestone (UP)", color: "#C6F2F0" }
];

const dataSouthAfrica2025 = [
  { category: "Library Discovery & AI Ecosystem", count: 4, examples: "Ex Libris Primo, Rapido, Rialto, Primo RA (UNIZULU)", color: "#0A1A2F" },
  { category: "Student Payments", count: 3, examples: "Flywire (UKZN), PayGate (DUT), Adumo (CUT)", color: "#1C485C" },
  { category: "SIS & Housing", count: 2, examples: "Anthology Student (NWU), StarRez (NWU)", color: "#3D8C91" },
  { category: "Video & Lecture Capture", count: 1, examples: "Panopto (CUT)", color: "#56B0AF" },
  { category: "Research Data Management", count: 2, examples: "Figshare (UFH, UL)", color: "#89CDD4" },
  { category: "E-Assessment & Proctoring", count: 1, examples: "Inspera (NMU)", color: "#C6F2F0" }
];

const commentaryAustralia2024 = "Targeted digital modernization (ANU, UTS, USyd, UOW).";
const commentaryAustralia2025 = "Large-scale SaaS shift toward assessment modernization, LMS upgrades, and research back-office cloudification.";

const commentaryHongKong2024 = "New wins were low-friction add-ons led by Ex Libris (Primo RA) and FeedbackFruits (Automated Feedback pilots). Okta and Zoom show one-off hygiene buys. Net: Library/T&L budgets, pilot-first landings, no core swaps.";
const commentaryHongKong2025 = "Pattern intensifies: Ex Libris keeps AI overlays; Turnitin adds logos via Gradescope/FBS pilots; Elsevier Pure signals research admin upgrades. Net: pilots remain the entry path—win by converting pilots to multi-year, multi-faculty rollouts.";

const commentaryUK2024 = "2024 buying centred on teaching-related systems (evaluation, portals, and learning support), with Explorance Blue and EvaSys leading adoption. A secondary wave targeted cybersecurity and core infrastructure tools like ITSM and CRIS, reflecting post-pandemic stabilisation rather than new pedagogy platforms.";
const commentaryUK2025 = "In 2025, purchasing shifted toward financial and operational platforms—notably international payment solutions and cloud integrations—signalling a focus on automation and efficiency. Academic-side buying narrowed to a few targeted upgrades (Panopto renewals, new peer-learning and ethics systems) rather than broad LMS or engagement investments.";

const commentarySouthAfrica2024 = "2024 spending concentrated on research management, CRM modernisation, and library system upgrades, forming the backbone of digital transformation initiatives. Secondary momentum areas included cybersecurity, AI-driven academic tools, and digital credentialing, signalling a broader shift to cloud-first services.";
const commentarySouthAfrica2025 = "2025 was dominated by library ecosystem replacements, payment modernization, and SIS/housing system renewal, with Ex Libris and Anthology clearly leading market share. Additional growth emerged in video capture, research data platforms, and e-assessment, reflecting universities' need to modernize end-to-end digital infrastructure.";

// Skillwell data (Fortune 500 enterprises)
const dataSkillwell2024 = [
  { category: "LXP / Learning Delivery Platform", count: 1, examples: "S&P Global → Accenture LearnVantage", color: "#0A1A2F" },
  { category: "Skills / Talent Intelligence Platform", count: 1, examples: "Accenture → Workera", color: "#1C485C" },
  { category: "Acquired L&D Content Providers", count: 2, examples: "Accenture acquires Udacity, Award Solutions", color: "#3D8C91" },
  { category: "University / Education Partners", count: 2, examples: "Amazon Career Choice: FSU, Shepherd University", color: "#56B0AF" },
  { category: "Specialist Training / Certification Partners", count: 1, examples: "Accenture + Stanford Online", color: "#89CDD4" },
  { category: "GenAI Co-pilot for Learning", count: 0, examples: "No new GenAI co-pilot purchases in 2024", color: "#C6F2F0" },
  { category: "Content Authoring Platforms", count: 0, examples: "No new authoring tool adoptions", color: "#D1D5DB" }
];

const dataSkillwell2025 = [
  { category: "Skills / Talent Intelligence Platform", count: 1, examples: "S&P Global → Eightfold AI", color: "#0A1A2F" },
  { category: "GenAI Co-pilot Adopted for Learning", count: 1, examples: "Accenture → ChatGPT Enterprise (OpenAI)", color: "#1C485C" },
  { category: "Acquired L&D Content Providers", count: 3, examples: "Accenture acquires TalentSprint, Ascendient Learning, Aidemy", color: "#3D8C91" },
  { category: "University / Education Partners", count: 1, examples: "Amazon Career Choice: USI", color: "#56B0AF" },
  { category: "Specialist Training / Certification Partners", count: 2, examples: "Accenture + SAP Learning; Amazon + Unmanned Safety Institute", color: "#89CDD4" },
  { category: "LXP / Learning Delivery Platform", count: 0, examples: "No new LXP adoptions (LearnVantage continues)", color: "#C6F2F0" },
  { category: "Content Authoring Platforms", count: 0, examples: "No new authoring tool adoptions", color: "#D1D5DB" }
];

const commentarySkillwell2024 = "2024 saw one big new LXP relationship (S&P → LearnVantage), one skills-intelligence buy (Accenture → Workera), and first content M&A (Udacity, Award Solutions). A steady trickle of new universities into Amazon Career Choice, plus Stanford Online as an elite curriculum partner.";
const commentarySkillwell2025 = "2025 shifted up-stack: talent intelligence (Eightfold AI) and GenAI co-pilots (ChatGPT Enterprise) emerged as key categories. M&A accelerated with three more content providers joining Accenture LearnVantage. Amazon continued adding universities and specialist providers to Career Choice.";

// Kairadigital data (Manufacturing/Retail/Pharma enterprises)
const dataKairadigital2024 = [
  { category: "Supply chain planning & optimisation", count: 4, examples: "adidas → o9 Solutions; AstraZeneca → OMP; Unilever → Oracle; Nestlé → OMP", color: "#0A1A2F" },
  { category: "Sustainability analytics (media/web)", count: 3, examples: "L'Oréal → IMPACT+, ADGREEN, FRUGGR", color: "#1C485C" },
  { category: "Logistics & DC operations", count: 2, examples: "adidas → DHL, Kuehne+Nagel", color: "#3D8C91" },
  { category: "R&D / AI drug development", count: 2, examples: "Sanofi → Formation Bio, OpenAI", color: "#56B0AF" },
  { category: "Warehouse & transportation mgmt", count: 1, examples: "Schneider → Manhattan Active WM/TM", color: "#89CDD4" },
  { category: "ERP / core enterprise cloud", count: 1, examples: "Schneider → SAP S/4HANA", color: "#C6F2F0" },
  { category: "Industrial decarbonisation", count: 1, examples: "H&M → Rondo Energy", color: "#0B0F1A" },
  { category: "Store & edge infrastructure", count: 1, examples: "H&M → Avassa", color: "#102838" },
  { category: "Marketing / CRM", count: 1, examples: "H&M → Voyado; L'Oréal → Meta", color: "#286D7F" },
  { category: "Supply chain digital twin", count: 1, examples: "Sanofi → Bluecrux", color: "#3D8C91" },
  { category: "Lab procurement", count: 1, examples: "Sanofi → ZAGENO", color: "#56B0AF" },
  { category: "IT services & outsourcing", count: 1, examples: "Ingka → Infosys", color: "#89CDD4" },
  { category: "Logistics innovation", count: 1, examples: "Ingka → Waabi", color: "#C6F2F0" },
  { category: "GenAI enablement", count: 1, examples: "Unilever → Accenture", color: "#0B0F1A" },
  { category: "Productivity & sales AI", count: 1, examples: "Schneider → Microsoft Copilot", color: "#102838" }
];

const dataKairadigital2025 = [
  { category: "Content & digital-twin platforms", count: 2, examples: "Unilever → NVIDIA Omniverse; Nestlé → NVIDIA Omniverse", color: "#0A1A2F" },
  { category: "Cloud & genAI platforms", count: 1, examples: "L'Oréal → Google Cloud", color: "#1C485C" },
  { category: "AI infrastructure & platforms", count: 1, examples: "L'Oréal → NVIDIA AI Enterprise", color: "#3D8C91" },
  { category: "Sustainability / waste analytics", count: 1, examples: "Nestlé → Zest", color: "#56B0AF" },
  { category: "Digital experience / content services", count: 1, examples: "Nestlé → Accenture Song", color: "#89CDD4" },
  { category: "Cloud infrastructure", count: 1, examples: "Nestlé → Microsoft", color: "#C6F2F0" },
  { category: "E-commerce & digital channels", count: 1, examples: "H&M → Centra", color: "#0B0F1A" },
  { category: "Warehouse automation & robotics", count: 1, examples: "H&M → Rebl Industries", color: "#102838" },
  { category: "AI research & talent ecosystem", count: 1, examples: "Unilever → Vector Institute", color: "#286D7F" },
  { category: "Commercial / CRM", count: 1, examples: "AstraZeneca → Salesforce Agentforce", color: "#3D8C91" },
  { category: "Transport mgmt / last-mile", count: 1, examples: "Ingka → Locus", color: "#56B0AF" },
  { category: "Clinical development AI", count: 1, examples: "Sanofi → QuantHealth", color: "#89CDD4" },
  { category: "Industrial AI / copilots", count: 1, examples: "Schneider → Microsoft Industrial Copilot", color: "#C6F2F0" },
  { category: "PLM / engineering platform", count: 1, examples: "Volkswagen → Dassault 3DEXPERIENCE", color: "#0B0F1A" }
];

const commentaryKairadigital2024 = "Heavy concentration in planning and DC/logistics infrastructure, plus early but meaningful moves into AI for R&D (Sanofi) and media/ESG analytics (L'Oréal, H&M).";
const commentaryKairadigital2025 = "Clear swing toward AI + digital twins for content and sustainability, plus industrial / domain-specific copilots (Schneider, clinical for Sanofi, engineering for VW).";

// Plataformaa 2024 data
const dataPlataformaa2024 = [
  { category: "Content Creation & Media Production", count: 2, examples: "UNAM (Adobe Creative Cloud), UFRO (Adobe Premiere Pro)", color: "#0A1A2F" },
  { category: "Immersive Simulation / AR/VR", count: 1, examples: "Universidad Distrital (Unity simulation platform)", color: "#1C485C" },
  { category: "Workforce Skills / Micro-credentials", count: 1, examples: "UdeG (Coursera Career Academy - 250 licenses)", color: "#3D8C91" },
  { category: "Online Assessment", count: 1, examples: "UNAD (3-year platform license)", color: "#56B0AF" }
];

// Plataformaa 2025 data (minimal confirmed awards)
const dataPlataformaa2025 = [
  { category: "Cloud + Virtual Assistant", count: 1, examples: "UNAL (eInnovation platform - pipeline)", color: "#0A1A2F" }
];

const commentaryPlataformaa2024 = "2024 dominated by content creation (40%) with Adobe winning at UNAM and UFRO. Unity secured immersive simulation at Universidad Distrital. Workforce upskilling via Coursera at UdeG and online assessment infrastructure at UNAD rounded out the year. Total: 5 confirmed vendor deployments.";
const commentaryPlataformaa2025 = "2025 shows minimal new platform awards. UNAL's eInnovation cloud + virtual assistant procurement remains in pipeline. Focus appears to be on scaling 2024 investments rather than new procurements.";

// Wayground 2024 data
const dataWayground2024 = [
  { category: "Devices & Classroom Hardware", count: 3, examples: "Jalisco (laptops, displays, robotics), Nuevo León (tablets, desktops), Chihuahua (computer equipment)", color: "#0A1A2F" },
  { category: "Connectivity & Infrastructure", count: 2, examples: "Nuevo León (school connectivity - 20 schools), UGEL 03 (storage solution)", color: "#1C485C" },
  { category: "Learning & Collaboration", count: 1, examples: "Baja California (Google Workspace for Education)", color: "#3D8C91" },
  { category: "Assessment & Certification", count: 1, examples: "Nuevo León (TOEIC + GLCC exam licenses)", color: "#56B0AF" },
  { category: "Teacher Development", count: 1, examples: "Nuevo León (Virtual English certification for PRONI coaches)", color: "#89CDD4" }
];

// Wayground 2025 data
const dataWayground2025 = [
  { category: "Devices & Classroom Hardware", count: 3, examples: "Plan Ceibal/ANEP (laptops, tablets), Jalisco (classroom hardware bundle), Chihuahua (computer equipment)", color: "#0A1A2F" },
  { category: "Learning & Collaboration", count: 2, examples: "Plan Ceibal/ANEP (national education platform), Jalisco (classroom software)", color: "#1C485C" },
  { category: "Admin & Support Platforms", count: 2, examples: "Plan Ceibal/ANEP (integrated info management), SLEP Atacama (Special-Ed platform)", color: "#3D8C91" },
  { category: "Assessment & Certification", count: 1, examples: "Nuevo León (ITEP Placement-Core - 1,300 licenses)", color: "#56B0AF" }
];

const commentaryWayground2024 = "2024 procurement is dominated by devices and classroom hardware (38%), with Jalisco and Nuevo León driving multi-line hardware bundles. Connectivity and infrastructure investments (25%) focus on school internet and storage solutions. Strategic platform buys remain limited, with Baja California's Google Workspace representing the main collaborative platform investment.";
const commentaryWayground2025 = "2025 marks a shift toward system-level platforms: learning and collaboration platforms (25%) and admin/support platforms (25%) together match device spending (38%). Plan Ceibal/ANEP leads with national education infrastructure, while SLEP Atacama invests in special-education management. Assessment certification continues strong with Nuevo León's ITEP diagnostic licenses.";

// Learnwise data (academic integrity / originality focus)
const dataLearnwise2024 = [
  { category: "Academic Integrity / Originality", count: 3, examples: "Mapúa (Turnitin + Draft Coach); University of the Republic (Compilatio); UFES (Turnitin Similarity)", color: "#0A1A2F" },
  { category: "Curriculum Management", count: 1, examples: "University of Technology Sydney (CourseLoop)", color: "#1C485C" },
  { category: "Cloud / Skills Platform", count: 1, examples: "Universiti Malaya (Alibaba Cloud Academic Empowerment + Academy Skills Centre)", color: "#3D8C91" },
  { category: "LMS", count: 1, examples: "Miriam College (Canvas LMS)", color: "#56B0AF" }
];

const dataLearnwise2025 = [
  { category: "Academic Integrity / Originality", count: 6, examples: "UCEN (Clarivate/IDEA); UASLP (iThenticate); UFU (Plagius); UTFSM (Compilatio); UFES (Turnitin AI Writing); UFBA (Turnitin Similarity Add-On)", color: "#0A1A2F" }
];

const commentaryLearnwise2024 = "2024 buying was concentrated in academic integrity (Mapúa, University of the Republic, UFES), with notable moves outside that category: UTS into curriculum management (CourseLoop), Universiti Malaya into Alibaba Cloud skills programs, and Miriam College to Canvas LMS.";
const commentaryLearnwise2025 = "2025 continued the academic-integrity pattern: UCEN (Clarivate), UASLP (iThenticate), UFU (Plagius), UTFSM (Compilatio), UFES (Turnitin AI Writing), UFBA (Turnitin Similarity Add-On). All six named purchases were integrity or originality tools. UFES appears in both years—Turnitin Similarity in 2024, then Turnitin AI Writing module in 2025.";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-[#0A1A2F] text-white p-3 rounded-lg shadow-lg max-w-sm border border-[#00C2C2]">
        <p className="font-semibold text-sm mb-1">{data.name}</p>
        <p className="text-xs text-[#00C2C2] mb-2">
          {data.payload.percentage ? `${data.payload.percentage}%` : `${data.value} New Deployments`}
        </p>
        <p className="text-xs">{data.payload.examples}</p>
      </div>
    );
  }
  return null;
};

export default function KeyBuyingAreas({ market }) {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState('2025');
  
  const isHongKong = market === 'hong-kong';
  const isUK = market === 'united-kingdom';
  const isSouthAfrica = market === 'south-africa';
  const isSkillwell = market === 'skillwell';
  const isKairadigital = market === 'kairadigital';
  const isWayground = market === 'wayground';
  const isPlataformaa = market === 'plataformaa';
  const isLearnwise = market === 'learnwise';
  
  let data2024, data2025, commentary2024, commentary2025;
  
  if (isLearnwise) {
    data2024 = dataLearnwise2024;
    data2025 = dataLearnwise2025;
    commentary2024 = commentaryLearnwise2024;
    commentary2025 = commentaryLearnwise2025;
  } else if (isPlataformaa) {
    data2024 = dataPlataformaa2024;
    data2025 = dataPlataformaa2025;
    commentary2024 = commentaryPlataformaa2024;
    commentary2025 = commentaryPlataformaa2025;
  } else if (isWayground) {
    data2024 = dataWayground2024;
    data2025 = dataWayground2025;
    commentary2024 = commentaryWayground2024;
    commentary2025 = commentaryWayground2025;
  } else if (isKairadigital) {
    data2024 = dataKairadigital2024;
    data2025 = dataKairadigital2025;
    commentary2024 = commentaryKairadigital2024;
    commentary2025 = commentaryKairadigital2025;
  } else if (isSkillwell) {
    data2024 = dataSkillwell2024;
    data2025 = dataSkillwell2025;
    commentary2024 = commentarySkillwell2024;
    commentary2025 = commentarySkillwell2025;
  } else if (isUK) {
    data2024 = dataUK2024;
    data2025 = dataUK2025;
    commentary2024 = commentaryUK2024;
    commentary2025 = commentaryUK2025;
  } else if (isHongKong) {
    data2024 = dataHongKong2024;
    data2025 = dataHongKong2025;
    commentary2024 = commentaryHongKong2024;
    commentary2025 = commentaryHongKong2025;
  } else if (isSouthAfrica) {
    data2024 = dataSouthAfrica2024;
    data2025 = dataSouthAfrica2025;
    commentary2024 = commentarySouthAfrica2024;
    commentary2025 = commentarySouthAfrica2025;
  } else {
    data2024 = dataAustralia2024;
    data2025 = dataAustralia2025;
    commentary2024 = commentaryAustralia2024;
    commentary2025 = commentaryAustralia2025;
  }
  
  const data = selectedYear === '2025' ? data2025 : data2024;

  return (
    <Card className="border-0 shadow-md h-full">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#00C2C2]" />
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('keyBuyingAreas')}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={selectedYear === '2024' ? 'default' : 'outline'}
              onClick={() => setSelectedYear('2024')}
              className={selectedYear === '2024' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2024
            </Button>
            <Button
              size="sm"
              variant={selectedYear === '2025' ? 'default' : 'outline'}
              onClick={() => setSelectedYear('2025')}
              className={selectedYear === '2025' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2025
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#5C6B7A]">{t('functionalInvestment')}</p>
        <p className="text-xs text-[#5C6B7A] mt-1">Illustrative sample — not live market intelligence for this tenant.</p>
        {isUK && (
          <p className="text-xs text-[#5C6B7A] italic mt-1">(data is based on 16 non-Canvas universities)</p>
        )}
      </CardHeader>
      <CardContent className="p-3 md:p-6">
        <div className="h-[200px] md:h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percent, percentage }) => {
                  const displayPercent = percentage || (percent * 100);
                  return `${displayPercent.toFixed(0)}%`;
                }}
                outerRadius={75}
                fill="#8884d8"
                dataKey="count"
                nameKey="category"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Custom legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 justify-center mt-3 mb-4">
          {data.map((entry, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <div 
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[9px] md:text-[10px] text-[#0A1A2F] leading-tight">
                {entry.category}
              </span>
            </div>
          ))}
        </div>
        
        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-semibold text-[#0A1A2F] mb-1">{t('headlineTrends')}</p>
          <p className="text-xs text-[#5C6B7A] leading-relaxed">
            {selectedYear === '2024' ? commentary2024 : commentary2025}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}